import { breakingNewRecordsFragment } from '@/utils/graphql/breakingNewRecordsFragment';
import { BreakingNew, BreakingNewsWithThreads, Channel } from '../model';
import { getAPILocale } from '@/utils';
import { getBreakingNewsRecords } from '../api';
import { minifyGraphQL } from './minifyGraphql';
import dayjs from 'dayjs';

function queryKeyGenerator(id: string) {
  return `l${id}`;
}

export async function setBreakingNewsRecords(
  channel: Channel,
  breakingNews: Array<BreakingNew>,
) {
  const existingBreakingNewsId: Record<string, boolean> = {};

  if (breakingNews.length === 0) {
    return [];
  }
  /**
   * Generate dynamic Query to fetch all missing records in one API call
   */
  const { dynamicQueryFragment, rootVariables, dynamicVariables } =
    breakingNews.reduce(
      (acc, news, index) => {
        if (news?.id in existingBreakingNewsId) {
          return acc;
        }
        existingBreakingNewsId[news.id] = true;
        const whereKey = `$where${index}`;
        return {
          dynamicQueryFragment: `${acc.dynamicQueryFragment}\n 
        ${queryKeyGenerator(news.id)}:BreakingNewsRecords(
           where: ${whereKey}
           locale: ${getAPILocale(channel.languageCode)}
           page: 1
           sort: $sort
         ) {
          ${breakingNewRecordsFragment}
       }`,
          rootVariables: `
           ${acc.rootVariables}\n
           ${whereKey}: BreakingNewsRecord_where
       `,
          dynamicVariables: {
            ...acc.dynamicVariables,
            [`where${index}`]: {
              channel: { equals: channel.id },
              breakingNewsId: { equals: news.id },
            },
          },
        };
      },
      {
        dynamicQueryFragment: '',
        rootVariables: '$sort: String\n',
        dynamicVariables: {
          sort: '-isPinned,pinIndex,-timestamp,-createdAt',
        },
      },
    );

  const dynamicQuery = minifyGraphQL(`query GetBreakingNewsRecord(
  ${rootVariables}
 ) {
   ${dynamicQueryFragment}
 }`);

  /**
   * Call dynamic Query to get the live coverage records
   */
  const breakingNewsRecords = await getBreakingNewsRecords({
    dynamicQuery,
    dynamicVariables,
  });

  if (breakingNewsRecords) {
    (breakingNews as unknown as Array<BreakingNewsWithThreads>).forEach(
      news => {
        const newsKey = queryKeyGenerator(news.id);
        news.threads =
          newsKey in breakingNewsRecords
            ? breakingNewsRecords[newsKey].docs
            : [];
      },
    );
  }

  return breakingNewsRecords;
}

const dateDiffAsMinutes = (date?: string): number => {
  const now = dayjs();
  const startTime = dayjs(date);
  return now.diff(startTime, 'minute');
};

export function filterBreakingNews(breakingNews?: Array<BreakingNew>) {
  if (!breakingNews) return [];
  const filteredNews =
    breakingNews
      ?.filter(b => {
        return (
          b.isPinned || (b.timestamp && dateDiffAsMinutes(b?.timestamp) < 10)
        );
      })
      ?.sort((a, b) => {
        const dateA = new Date(a.timestamp as string).getTime();
        const dateB = new Date(b.timestamp as string).getTime();
        return dateB - dateA;
      }) || [];

  return filteredNews;
}
